import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { GcLineItemBillingMethod, GcLineItemType } from "@/hooks/use-gc-line-items";
import { apiDelete, apiFetch, apiPatch, apiPost } from "@/lib/api";
import { gcLineItemKeys, gcLineItemTemplateKeys } from "@/lib/query";

export interface GcLineItemTemplateRow {
  id: string;
  templateId: string;
  lineType: GcLineItemType;
  label: string;
  billingMethod: GcLineItemBillingMethod;
  percentOfHardCosts: string | null;
  ownerFacing: boolean;
  retainageApplies: boolean;
  retentionPercentOverride: string | null;
  costCodeId: string | null;
  displayOrder: number;
  requiresReasonPerDraw: boolean;
  notes: string | null;
}

export interface GcLineItemTemplate {
  id: string;
  orgId: string;
  name: string;
  description: string | null;
  isDefault: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  rows: GcLineItemTemplateRow[];
}

export interface GcLineItemTemplateRowInput {
  lineType: GcLineItemType;
  label: string;
  billingMethod: GcLineItemBillingMethod;
  percentOfHardCosts?: string | null;
  ownerFacing?: boolean;
  retainageApplies?: boolean;
  retentionPercentOverride?: string | null;
  costCodeId?: string | null;
  displayOrder?: number;
  requiresReasonPerDraw?: boolean;
  notes?: string | null;
}

export interface GcLineItemTemplateInput {
  name: string;
  description?: string | null;
  isDefault?: boolean;
  isActive?: boolean;
  rows: GcLineItemTemplateRowInput[];
}

export function useGcLineItemTemplates() {
  return useQuery({
    queryKey: gcLineItemTemplateKeys.list(),
    queryFn: async () => {
      const res = await apiFetch<{ data: GcLineItemTemplate[] }>("/gc-line-item-templates");
      return res.data;
    },
  });
}

export function useGcLineItemTemplate(id: string | null) {
  return useQuery({
    queryKey: gcLineItemTemplateKeys.detail(id ?? ""),
    queryFn: async () => {
      const res = await apiFetch<{ data: GcLineItemTemplate }>(`/gc-line-item-templates/${id}`);
      return res.data;
    },
    enabled: !!id,
  });
}

export function useCreateGcLineItemTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: GcLineItemTemplateInput) => {
      const res = await apiPost<{ data: GcLineItemTemplate }>("/gc-line-item-templates", input);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: gcLineItemTemplateKeys.all });
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useUpdateGcLineItemTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: GcLineItemTemplateInput }) => {
      const res = await apiPatch<{ data: GcLineItemTemplate }>(
        `/gc-line-item-templates/${id}`,
        input,
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: gcLineItemTemplateKeys.all });
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useDeleteGcLineItemTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiDelete(`/gc-line-item-templates/${id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: gcLineItemTemplateKeys.all });
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useApplyGcLineItemTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ templateId, projectId }: { templateId: string; projectId: string }) => {
      const res = await apiPost<{ data: { inserted: number } }>(
        `/gc-line-item-templates/${templateId}/apply`,
        { projectId },
      );
      return res.data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: gcLineItemKeys.project(vars.projectId) });
      qc.invalidateQueries({ queryKey: gcLineItemKeys.tracker(vars.projectId) });
    },
    onError: (err: Error) => toast.error(err.message),
  });
}
